import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import * as ExploreKeys from '../reactQueryKeys/explore'

import { EmptyArray } from 'lib/array/arrayUtils'
import type { ExploreNearbyParams, ExploreParams } from 'tripPlanner/api/explore'
import { getExploreItems, getExploreNearbyItems } from 'tripPlanner/api/explore'
import type {
  GetExploreItemsError,
  GetExploreItemsResponse,
  GetExploreNearbyItemsError,
  GetExploreNearbyItemsResponse,
} from 'tripPlanner/api/explore/types'
import type { ExploreItem } from 'tripPlanner/types/common'
import type { UseExploreItemsParams, UseExploreNearbyItemsParams } from './exploreParams'

const MINUTES = 60 * 1000
const HOURS = 60 * MINUTES
const STALE_TIME = 24 * HOURS
/**
 * Explicit, not inherited: "View on map" replays the drawer's query to hit this cache, and the
 * drawer closes before the map reads it, which starts the GC timer.
 */
const CACHE_TIME = 30 * MINUTES

function paramsHaveQuery(
  params: UseExploreItemsParams,
): params is ExploreParams {
  return Boolean(params.query) && Boolean(params.locationBias)
}

function paramsHaveLocation(
  params: UseExploreNearbyItemsParams,
): params is ExploreNearbyParams {
  return Boolean(params.location)
}

export const useExploreItems = (
  params: UseExploreItemsParams,
  options?: UseQueryOptions<
    GetExploreItemsResponse['result'],
    GetExploreItemsError,
    Array<ExploreItem>
  >,
) => {
  return useQuery({
    queryKey: ExploreKeys.items(params),
    queryFn: () =>
      paramsHaveQuery(params) ?
          getExploreItems(params) :
          Promise.resolve({
            items: EmptyArray,
          }),
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    select: (data) => data.items,
    ...options,
  })
}

export const useExploreNearbyItems = (
  params: UseExploreNearbyItemsParams,
  options?: UseQueryOptions<
    GetExploreNearbyItemsResponse['result'],
    GetExploreNearbyItemsError,
    Array<ExploreItem>
  >,
) => {
  return useQuery({
    queryKey: ExploreKeys.nearbyItems(params),
    queryFn: () =>
      paramsHaveLocation(params) ?
          getExploreNearbyItems(params) :
          Promise.resolve({
            items: EmptyArray,
          }),
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    select: (data) => data.items,
    ...options,
  })
}
